import { Box, IconButton, ListItemIcon, Menu, MenuItem, TextField, Typography } from "@mui/material";
import Archive from "@mui/icons-material/Archive";
import Unarchive from "@mui/icons-material/Unarchive";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import { useAsyncAction } from "nate-react-api-helpers";
import { useEffect, useRef, useState } from "react";
import { api } from "../../api/API";
import { Goal, GoalInput } from "../../api/Projects";
import { Task } from "../../api/Tasks";
import { ConfirmDialog } from "../../misc/ConfirmDialog";
import { HtmlEditor } from "../../misc/HtmlEditor";
import { panelFontSize } from "../task/panel/PanelSection";
import { useSnackAction } from "../../misc/SnackAction";
import { useSyncedRef } from "../../misc/SyncedRef";
import { TaskCrumbs, TrailStep } from "../task/panel/TaskCrumbs";
import { GoalTasks } from "./GoalTasks";

// let typing settle before an auto-save goes out
const textDebounceMs = 800;

// GoalPanel edits one piece of a project. A goal is never completed, only archived,
// and archiving it takes its open tasks with it.
export function GoalPanel(props: {
    value: Goal;
    onClose: () => void;

    // onOpenTask hands one of the goal's tasks to whoever owns the panel slot, which
    // swaps this panel out for the task editor
    onOpenTask: (task: Task) => void;

    // ancestors is what the page opened this panel out of, outermost first
    ancestors?: TrailStep[];
}) {
    const [name, setName] = useState(props.value.name);
    const [description, setDescription] = useState(props.value.description);
    const [confirming, setConfirming] = useState(false);

    // what the archive would take with it, which the task list is what reports (the
    // count the goal was read with is only as fresh as the panel's last fetch)
    const [openCount, setOpenCount] = useState(props.value.openCount);

    const input: GoalInput = {
        id: props.value.id,
        project: props.value.project,
        name: name,
        description: description,

        // carried so an edit to an archived goal doesn't read as un-archiving it
        archived: props.value.archived,
    };

    const inputRef = useSyncedRef(input);

    const trail = (props.ancestors || []).concat({ title: name });

    const archive = useSnackAction(async () => {
        await api.projects.upsertGoal({ ...inputRef.current, archived: true });
        setConfirming(false);
        props.onClose();
    }, { success: "Goal archived" });

    // un-archiving puts back the tasks the archive took, and nothing else
    const restore = useSnackAction(async () => {
        await api.projects.upsertGoal({ ...inputRef.current, archived: false });
        props.onClose();
    }, { success: "Goal restored" });

    // an auto-save carrying no change is just noise, so compare against what the
    // server was last given
    const saved = useRef(JSON.stringify(input));

    const autoSave = useAsyncAction(async () => {
        const posted = inputRef.current;
        await api.projects.upsertGoal(posted);
        saved.current = JSON.stringify(posted);
    }, []);

    const autoSaveRef = useSyncedRef(autoSave.callback);
    const pendingText = useRef(false);
    const started = useRef(false);

    useEffect(() => {
        if (!started.current) {
            started.current = true;
            return;
        }

        pendingText.current = true;
        const timer = setTimeout(() => {
            pendingText.current = false;
            if (JSON.stringify(inputRef.current) !== saved.current) autoSaveRef.current(null);
        }, textDebounceMs);

        return () => clearTimeout(timer);
    }, [name, description, inputRef, autoSaveRef]);

    // don't drop an edit that was still waiting when the panel closed
    const flush = useRef(() => {});
    flush.current = () => {
        if (!pendingText.current) return;
        if (JSON.stringify(inputRef.current) !== saved.current) autoSaveRef.current(null);
    };

    useEffect(() => () => flush.current(), []);

    const [menu, setMenu] = useState<HTMLElement | null>(null);

    let status = "";
    if (autoSave.loading) status = "Saving...";
    else if (autoSave.error) status = autoSave.error;

    return (
        <Box style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 3, pr: 1, pt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    Goal
                </Typography>
                {props.value.archived && (
                    <Typography variant="caption" color="warning.dark" sx={{ flexShrink: 0 }}>
                        Archived
                    </Typography>
                )}
                <TaskCrumbs value={trail} />
                <Typography variant="caption" noWrap
                            color={autoSave.error ? "error.main" : "text.secondary"}
                            style={{ flex: trail.length > 1 ? undefined : 1 }}>
                    {status}
                </Typography>
                <IconButton size="small" onClick={props.onClose}>
                    <KeyboardDoubleArrowRight fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 3, pb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <TextField
                        placeholder="Goal name"
                        variant="standard"
                        fullWidth
                        autoFocus
                        value={name}
                        onChange={e => setName(e.target.value)}
                        slotProps={{ input: { disableUnderline: true, style: { fontSize: 24, fontWeight: 500 } } }}
                    />
                    <IconButton size="small" aria-label="Goal menu" sx={{ flexShrink: 0, mt: 0.5, mr: -2 }}
                                disabled={archive.loading || restore.loading}
                                onClick={e => setMenu(e.currentTarget)}>
                        <MoreHoriz fontSize="small" />
                    </IconButton>
                    <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          transformOrigin={{ vertical: "top", horizontal: "right" }}
                          slotProps={{ paper: { sx: { "& .MuiMenuItem-root": { fontSize: panelFontSize, minHeight: 32 } } } }}>
                        {props.value.archived ? (
                            <MenuItem onClick={() => {
                                setMenu(null);
                                restore.callback(null);
                            }}>
                                <ListItemIcon><Unarchive fontSize="small" /></ListItemIcon>
                                Restore
                            </MenuItem>
                        ) : (
                            <MenuItem onClick={() => {
                                setMenu(null);
                                setConfirming(true);
                            }}>
                                <ListItemIcon><Archive fontSize="small" /></ListItemIcon>
                                Archive
                            </MenuItem>
                        )}
                    </Menu>
                </Box>

                <div style={{ height: 16 }} />
                <GoalTasks
                    goalId={props.value.id}
                    projectId={props.value.project}
                    onOpenTask={props.onOpenTask}
                    onOpenCount={setOpenCount}
                />

                <div style={{ height: 16 }} />
                {/* full-bleed: cancel the panel's horizontal padding */}
                <Box sx={{ mx: -3 }}>
                    <HtmlEditor placeholder="What does reaching this goal look like..."
                                fontSize={panelFontSize}
                                value={description} onChange={setDescription} />
                </Box>
            </Box>

            <ConfirmDialog
                open={confirming}
                title="Archive this goal?"
                message={openCount === 1
                    ? "Its 1 open task will be archived with it. Completed tasks are left alone."
                    : "Its " + openCount + " open tasks will be archived with it. "
                        + "Completed tasks are left alone."}
                confirmLabel="Archive"
                confirmColor="error"
                onConfirm={() => archive.callback(null)}
                onCancel={() => setConfirming(false)}
            />
        </Box>
    );
}
